import { apiFetch } from '@/stores/authStore';
import { downloadCsvResponse } from '@/lib/infra/csvExport';

export const EXPORT_TIMEOUT_MS = 120_000;

export interface CsvExportResult {
  filename: string;
  /** The relay's X-Export-Row-Count header, or null when absent. */
  rowCount: string | null;
}

/**
 * Fetch a relay CSV export and trigger the browser download, surfacing the four
 * recoverable failure modes (#126) as thrown Errors with concise messages:
 *   - HTTP / authorization failure → the server's message (exportErrorMessage);
 *   - timeout (stalled connection) → "Export timed out — retry";
 *   - malformed 200 (non-CSV body) → "Unexpected response (not a CSV export)".
 *
 * The abort timer is headers-only: it is cleared the instant the response
 * arrives, so a legitimately-streaming body download is never aborted — only a
 * connection that never responds trips it. Bytes are delivered verbatim
 * (BOM/CRLF preserved) via downloadCsvResponse.
 */
export async function fetchCsvExport(
  url: string,
  fallbackName: string,
  timeoutMs: number = EXPORT_TIMEOUT_MS,
): Promise<CsvExportResult> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  // Watchdog raced against the request: the signal cancels apiFetch's own
  // fetches, but not the token-refresh call apiFetch makes between a 401 and
  // its retry — if THAT hangs, apiFetch never settles and the export would sit
  // pending past the timeout. The race guarantees we settle when the timer
  // fires no matter where the request chain is stuck.
  const abortedWatchdog = new Promise<never>((_resolve, reject) => {
    controller.signal.addEventListener(
      'abort',
      () => reject(new DOMException('The operation was aborted.', 'AbortError')),
      { once: true },
    );
  });
  let res: Response;
  try {
    const request = apiFetch(url, { signal: controller.signal });
    // The loser of the race may reject later (the abort tears down the fetch);
    // swallow it so it can't surface as an unhandled rejection.
    request.catch(() => {});
    res = await Promise.race([request, abortedWatchdog]);
  } catch (err) {
    if (controller.signal.aborted) throw new Error('Export timed out — retry');
    throw err;
  } finally {
    clearTimeout(timer);
  }

  if (!res.ok) throw new Error(await exportErrorMessage(res));

  // Malformed 200: a gateway/auth HTML page or a JSON error would otherwise be
  // downloaded as a bogus ".csv". A 0-row export is still header-only text/csv.
  if (!(res.headers.get('content-type') ?? '').toLowerCase().includes('text/csv')) {
    throw new Error('Unexpected response (not a CSV export)');
  }

  const rowCount = res.headers.get('X-Export-Row-Count');
  const filename = await downloadCsvResponse(res, fallbackName);
  return { filename, rowCount };
}

async function exportErrorMessage(res: Response): Promise<string> {
  const text = await res.text().catch(() => '');
  if (text) {
    try {
      const data = JSON.parse(text) as { error?: unknown };
      if (typeof data.error === 'string') return conciseMessage(data.error, res.status);
    } catch {
      return conciseMessage(text, res.status);
    }
  }
  return `Export failed (${res.status})`;
}

/**
 * Keep the surfaced error concise: a proxy/gateway failure (e.g. nginx 502)
 * returns a whole HTML page as the body — never show markup, and cap length so
 * an unbounded body can't flood the banner.
 */
function conciseMessage(raw: string, status: number): string {
  const text = raw.trim();
  if (!text || text.startsWith('<')) return `Export failed (${status})`;
  return text.length > 200 ? `${text.slice(0, 200)}…` : text;
}
